/**
 * The two charts on the Billing & budget tab. Plain SVG, one y-axis each,
 * a hover layer on every month, and a visually-hidden table for screen readers.
 * Series colours come from the validated palette in model.ts.
 */
import { useState } from "react";
import { monthLabel, money } from "./model";

const W = 760;
const H = 260;
const L = 64;
const R = 16;
const T = 20;
const B = 32;
const PW = W - L - R;
const PH = H - T - B;
const THIS_YEAR = "#2a78d6";
const LAST_YEAR = "#9b958b";

/** A top for the y-axis whose four gridlines land on round numbers. */
function niceMax(v: number): number {
    if (v <= 0) return 400_00;
    const raw = v / 4;
    const pow = 10 ** Math.floor(Math.log10(raw));
    for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * pow >= raw) return m * pow * 4;
    return 40 * pow;
}

function axisLabel(cents: number): string {
    const d = cents / 100;
    if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(d % 1_000_000 ? 1 : 0)}M`;
    if (d >= 1000) return `$${Math.round(d / 1000)}k`;
    return `$${Math.round(d)}`;
}

interface Tip {
    x: number;
    y: number;
    html: string[];
}

function Tooltip({ tip }: { tip: Tip | null }) {
    if (!tip) return null;
    return (
        <div
            className="pointer-events-none absolute z-10 rounded-md bg-foreground px-2.5 py-1.5 text-[11.5px] leading-snug text-background shadow-lg"
            style={{ left: tip.x + 12, top: tip.y + 12 }}
        >
            {tip.html.map((l, i) => (
                <div key={i} className={i === 0 ? "font-semibold" : undefined}>
                    {l}
                </div>
            ))}
        </div>
    );
}

export interface CumulativeProps {
    months: Array<{ month: string; cents: number; lastYearCents: number }>;
    /** Index of the month containing today, or -1 when the range is in the past. */
    todayIndex: number;
    projectedCents: number | null;
    budgetCents: number | null;
}

/** Running total through the range: this year, last year, projection, budget line. */
export function CumulativeChart({ months, todayIndex, projectedCents, budgetCents }: CumulativeProps) {
    const [tip, setTip] = useState<Tip | null>(null);
    const n = months.length;
    const ty: number[] = [];
    const ly: number[] = [];
    let a = 0;
    let b = 0;
    for (const m of months) {
        a += m.cents;
        b += m.lastYearCents;
        ty.push(a);
        ly.push(b);
    }
    const shownTy = todayIndex >= 0 ? ty.slice(0, todayIndex + 1) : ty;
    // Projection: from today's point, last year's month-by-month adds.
    const proj: number[] = [];
    if (todayIndex >= 0 && projectedCents !== null && todayIndex < n - 1) {
        let p = ty[todayIndex]!;
        proj.push(p);
        for (let i = todayIndex + 1; i < n; i++) {
            p += months[i]!.lastYearCents;
            proj.push(p);
        }
    }
    const max = niceMax(Math.max(...ty, ...ly, ...proj, budgetCents ?? 0) * 1.05);
    const x = (i: number) => L + (i + 0.5) * (PW / n);
    const y = (v: number) => T + PH - (v / max) * PH;
    const line = (vals: number[], start = 0) =>
        vals.map((v, i) => `${x(start + i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);

    return (
        <div className="relative" onMouseLeave={() => setTip(null)}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                className="block h-auto w-full"
                role="img"
                aria-label="Running total billed through the period, this year against last year"
            >
                {ticks.map((v) => (
                    <g key={v}>
                        <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="stroke-border" strokeWidth={1} />
                        <text x={L - 8} y={y(v) + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
                            {axisLabel(v)}
                        </text>
                    </g>
                ))}
                {budgetCents !== null && (
                    <g>
                        <line
                            x1={L}
                            x2={W - R}
                            y1={y(budgetCents)}
                            y2={y(budgetCents)}
                            className="stroke-foreground"
                            strokeWidth={1.5}
                            strokeDasharray="2 3"
                        />
                        <text x={L + 6} y={y(budgetCents) - 6} className="fill-foreground text-[11.5px] font-bold">
                            Budget {money(budgetCents)}
                        </text>
                    </g>
                )}
                <polyline points={line(ly)} fill="none" stroke={LAST_YEAR} strokeWidth={2} strokeDasharray="6 5" />
                {proj.length > 1 && (
                    <polyline
                        points={line(proj, todayIndex)}
                        fill="none"
                        stroke={THIS_YEAR}
                        strokeWidth={2}
                        strokeDasharray="1 5"
                        strokeLinecap="round"
                    />
                )}
                <polyline points={line(shownTy)} fill="none" stroke={THIS_YEAR} strokeWidth={2.5} />
                {shownTy.map((v, i) => (
                    <circle
                        key={i}
                        cx={x(i)}
                        cy={y(v)}
                        r={4.5}
                        fill={THIS_YEAR}
                        className="stroke-card"
                        strokeWidth={2}
                    />
                ))}
                {months.map((m, i) => (
                    <text
                        key={m.month}
                        x={x(i)}
                        y={H - 10}
                        textAnchor="middle"
                        className="fill-muted-foreground text-[11px]"
                    >
                        {monthLabel(m.month)}
                    </text>
                ))}
                {months.map((m, i) => (
                    <rect
                        key={`hit-${m.month}`}
                        x={L + i * (PW / n)}
                        y={T}
                        width={PW / n}
                        height={PH}
                        fill="transparent"
                        className="cursor-crosshair hover:fill-foreground/5"
                        onMouseMove={(e) => {
                            const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                            const inFuture = todayIndex >= 0 && i > todayIndex;
                            setTip({
                                x: e.clientX - box.left,
                                y: e.clientY - box.top,
                                html: [
                                    `End of ${monthLabel(m.month)}`,
                                    inFuture
                                        ? proj[i - todayIndex] !== undefined
                                            ? `This year: ≈${money(proj[i - todayIndex]!)} projected`
                                            : "This year: —"
                                        : `This year: ${money(ty[i]!)}`,
                                    `Last year: ${money(ly[i]!)}`,
                                ],
                            });
                        }}
                    />
                ))}
            </svg>
            <Tooltip tip={tip} />
            <table className="sr-only">
                <caption>Running total by month</caption>
                <thead>
                    <tr>
                        <th>Month</th>
                        <th>This year</th>
                        <th>Last year</th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m, i) => (
                        <tr key={m.month}>
                            <td>{m.month}</td>
                            <td>{todayIndex >= 0 && i > todayIndex ? "—" : money(ty[i]!)}</td>
                            <td>{money(ly[i]!)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** What was invoiced each month, this year beside last year. */
export function MonthlyChart({ months }: { months: Array<{ month: string; cents: number; lastYearCents: number }> }) {
    const [tip, setTip] = useState<Tip | null>(null);
    const n = months.length;
    const max = niceMax(Math.max(...months.map((m) => Math.max(m.cents, m.lastYearCents))) * 1.1);
    const slot = PW / n;
    const bw = Math.max(3, slot / 2 - 5);
    const y = (v: number) => T + PH - (v / max) * PH;
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
    const show = (e: React.MouseEvent<SVGRectElement>, lines: string[]) => {
        const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
        setTip({ x: e.clientX - box.left, y: e.clientY - box.top, html: lines });
    };
    return (
        <div className="relative" onMouseLeave={() => setTip(null)}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                className="block h-auto w-full"
                role="img"
                aria-label="Billed by month, this year against last year"
            >
                {ticks.map((v) => (
                    <g key={v}>
                        <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="stroke-border" strokeWidth={1} />
                        <text x={L - 8} y={y(v) + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
                            {axisLabel(v)}
                        </text>
                    </g>
                ))}
                {months.map((m, i) => {
                    const x0 = L + i * slot + 4;
                    const hTy = (m.cents / max) * PH;
                    const hLy = (m.lastYearCents / max) * PH;
                    return (
                        <g key={m.month}>
                            {m.cents > 0 && (
                                <rect
                                    x={x0}
                                    y={T + PH - hTy}
                                    width={bw}
                                    height={hTy}
                                    rx={3}
                                    fill={THIS_YEAR}
                                    onMouseMove={(e) => show(e, [`${monthLabel(m.month)} this year`, money(m.cents)])}
                                />
                            )}
                            {m.lastYearCents > 0 && (
                                <rect
                                    x={x0 + bw + 2}
                                    y={T + PH - hLy}
                                    width={bw}
                                    height={hLy}
                                    rx={3}
                                    fill="#cfc8bc"
                                    onMouseMove={(e) =>
                                        show(e, [`${monthLabel(m.month)} last year`, money(m.lastYearCents)])
                                    }
                                />
                            )}
                            <text
                                x={L + (i + 0.5) * slot}
                                y={H - 10}
                                textAnchor="middle"
                                className="fill-muted-foreground text-[11px]"
                            >
                                {monthLabel(m.month)}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <Tooltip tip={tip} />
            <table className="sr-only">
                <caption>Billed by month</caption>
                <thead>
                    <tr>
                        <th>Month</th>
                        <th>This year</th>
                        <th>Last year</th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m) => (
                        <tr key={m.month}>
                            <td>{m.month}</td>
                            <td>{money(m.cents)}</td>
                            <td>{money(m.lastYearCents)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
